import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FilePlus } from "lucide-react";
import { toast } from "sonner";
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { accountOption, buildAccountPickerOptions, countDefaultPickerAccounts, hasCompanyAccounts, pinnedAccountValues, searchAccountOptions } from '../../../utils/accountPickerOptions';
import { listBookkeepingTemplates, recordTemplateUsage, getBookkeepingTemplate } from '../../../services/BookkeepingTemplateService';
import "./bookkeeping.css";
import PageHeader from "../../layouts/PageHeader";

import BookkeepingProgressTracker from "./BookkeepingProgressTracker";
import BookkeepingStepContent from "./BookkeepingStepContent";
import BookkeepingNavigationBar from "./BookkeepingNavigationBar";
import { getMissingRequired } from "./steps/DetailsEntryStep";

// Step definitions
const STEPS = {
    ENTRY_TYPE: 'step_1_entry_type',
    TEMPLATE: 'step_3_template',
    DETAILS: 'step_4_details',
    PREVIEW: 'step_5_preview'
};

const BookkeepingEntryWizard = () => {
    const { companyId } = useParams();

    // Data states
    const [templates, setTemplates] = useState([]);
    const [accounts, setAccounts] = useState([]);
    // "Visa alla konton" — the steps' pickers get the default view (OC-5);
    // lookups by number keep using the full list.
    const [showAllAccounts, setShowAllAccounts] = useState(false);

    // Loading states
    const [templatesLoading, setTemplatesLoading] = useState(true);
    const [accountsLoading, setAccountsLoading] = useState(true);
    const [templatesError, setTemplatesError] = useState(null);
    const [accountsError, setAccountsError] = useState(null);

    // UI states
    const [activeStep, setActiveStep] = useState(STEPS.ENTRY_TYPE);
    const [operationInProgress, setOperationInProgress] = useState(false);

    // Entry data state
    const [entry, setEntry] = useState({
        entryType: null,
        browseAllTemplates: false,
        template: null,
        templateData: null,
        attachments: [],
        date: new Date(),
        description: "",
        reference: "",
        notes: "",
        parameters: {},
        userDefinedAccounts: {},
        bookingProposal: null,
        customJournalEntries: null,
        templateIncompatibility: null
    });

    useEffect(() => {
        Promise.all([fetchTemplates(), fetchAccounts()]);
    }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Data fetching functions
    const fetchTemplates = useCallback(async () => {
        if (!companyId) {
            setTemplatesError("Företags-ID krävs");
            setTemplatesLoading(false);
            return;
        }

        try {
            setTemplatesLoading(true);
            // Routes through the new bookkeeping-templates service
            // (template-system-redesign). Returns rows with a `body`
            // JSON contract that the wizard's step-3 / step-4 branch
            // on (new-shape → TemplateForm + TemplatePreview; legacy
            // → existing DetailsEntryStep + PreviewStep).
            const rows = await listBookkeepingTemplates(companyId);
            if (Array.isArray(rows) && rows.length > 0) {
                const transformedTemplates = rows.map(template => ({
                    id: template.template_id,
                    name: template.template_name,
                    description: template.description,
                    eventFamily: template.event_family || mapTemplateToEventFamily(template),
                    // Prefer the backend-supplied taxonomy category
                    // (DEFERRED 59) when present; fall back to the
                    // derived bucket for older rows that pre-date the
                    // category column migration.
                    category: template.category || mapApiTypeToCategory(template.template_type),
                    common: isCommonTemplate(template.template_name),
                    // Per-tenant usage stats (DEFERRED 48) for the
                    // "Senast använda" picker section + frequency sort.
                    useCount: template.use_count || 0,
                    lastUsedAt: template.last_used_at || null,
                    accounts: extractAccountsFromTemplate(template),
                    originalData: template
                }));
                setTemplates(transformedTemplates);
                setTemplatesError(null);
            } else {
                setTemplates([]);
                setTemplatesError(null);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            setTemplatesError('Det gick inte att ladda mallar från API');
            toast.error('Det gick inte att ladda mallar');
            setTemplates([]);
        } finally {
            setTemplatesLoading(false);
        }
    }, [companyId]);

    const fetchAccounts = useCallback(async () => {
        if (!companyId) return;

        try {
            setAccountsLoading(true);
            setAccountsError(null);

            const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts?scope=company`);
            const response = await axios.get(accountsApiUrl, { withCredentials: true });

            if (response.data && response.data) {
                const formattedAccounts = response.data.accounts.map(account => ({
                    ...accountOption(account),
                    balance: account.balance || 0,
                    isActive: account.is_active !== false,
                }));

                formattedAccounts.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
                setAccounts(formattedAccounts);
            } else {
                // No invented fallback chart: an empty result is an error the
                // user can act on (retry), never a fake three-account list.
                setAccounts([]);
                setAccountsError('Kontoplanen kunde inte hämtas.');
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setAccountsError(error.message);
            setAccounts([]);
        } finally {
            setAccountsLoading(false);
        }
    }, [companyId]);

    // Navigation functions
    const handleStepClick = (stepKey) => {
        if (canNavigateToStep(stepKey) && !operationInProgress) {
            setActiveStep(stepKey);
        }
    };

    const handleStepChange = async (targetStep, direction = 'forward') => {
        if (operationInProgress) return;

        // Validate current step before proceeding
        if (direction === 'forward' && !validateCurrentStep()) {
            return;
        }

        setActiveStep(targetStep);
    };

    // Validation functions
    const validateCurrentStep = () => {
        switch (activeStep) {
            case STEPS.ENTRY_TYPE:
                if (!entry.entryType && !entry.browseAllTemplates) {
                    toast.error("Välj vad som har hänt eller visa alla mallar");
                    return false;
                }
                return true;
            case STEPS.TEMPLATE:
                if (!entry.template) {
                    toast.error("Välj en mall för att fortsätta");
                    return false;
                }
                return true;
            case STEPS.DETAILS: {
                // The step marks every gap once this flag is set, and the
                // toast names the first one so a novice knows where to look.
                const missing = getMissingRequired(entry);
                if (missing.length > 0) {
                    setEntry((prev) => ({ ...prev, showDetailsErrors: true }));
                    toast.error(`Fyll i ${missing[0].label.toLowerCase()} för att fortsätta`);
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    };

    const canNavigateToStep = (targetStep) => {
        const stepOrder = [STEPS.ENTRY_TYPE, STEPS.TEMPLATE, STEPS.DETAILS, STEPS.PREVIEW];
        const currentIndex = stepOrder.indexOf(activeStep);
        const targetIndex = stepOrder.indexOf(targetStep);

        // Can always go backwards
        if (targetIndex <= currentIndex) return true;

        // Check if previous steps are valid
        for (let i = 0; i < targetIndex; i++) {
            const step = stepOrder[i];
            if (!isStepCompleted(step)) return false;
        }

        return true;
    };

    const isStepCompleted = (step) => {
        switch (step) {
            case STEPS.ENTRY_TYPE:
                return !!entry.entryType || entry.browseAllTemplates === true;
            case STEPS.TEMPLATE:
                return !!entry.template;
            case STEPS.DETAILS:
                return !!entry.description && entry.description.trim() !== '';
            default:
                return false;
        }
    };

    const canProceedToNext = () => {
        const stepOrder = [STEPS.ENTRY_TYPE, STEPS.TEMPLATE, STEPS.DETAILS, STEPS.PREVIEW];
        const currentIndex = stepOrder.indexOf(activeStep);
        return currentIndex < stepOrder.length - 1 && isStepCompleted(activeStep);
    };

    const canGoBack = () => {
        const stepOrder = [STEPS.ENTRY_TYPE, STEPS.TEMPLATE, STEPS.DETAILS, STEPS.PREVIEW];
        const currentIndex = stepOrder.indexOf(activeStep);
        return currentIndex > 0;
    };

    const getNextStep = () => {
        const stepOrder = [STEPS.ENTRY_TYPE, STEPS.TEMPLATE, STEPS.DETAILS, STEPS.PREVIEW];
        const currentIndex = stepOrder.indexOf(activeStep);
        return stepOrder[currentIndex + 1];
    };

    const getPreviousStep = () => {
        const stepOrder = [STEPS.ENTRY_TYPE, STEPS.TEMPLATE, STEPS.DETAILS, STEPS.PREVIEW];
        const currentIndex = stepOrder.indexOf(activeStep);
        return stepOrder[currentIndex - 1];
    };

    // Entry creation handler
    const handleCreateVerification = async () => {
        try {
            setOperationInProgress(true);

            if (entry.templateData?.body
                && (!entry.bookingProposal || !entry.customJournalEntries
                    || entry.templateIncompatibility?.length)) {
                toast.error("Mallen är inte kompatibel med företagets kontoplan och kan inte bokföras");
                return;
            }

            // Validation
            if (!entry.description || entry.description.trim() === '') {
                toast.error("Beskrivning krävs");
                return;
            }

            if (!entry.date) {
                toast.error("Datum krävs");
                return;
            }

            console.log('Creating verification with entry:', entry);

            // Transform entry to verification format
            const verificationData = transformEntryToVerification(entry);

            console.log('Transformed verification data:', verificationData);

            // Validate transactions
            if (!verificationData.transactions || verificationData.transactions.length === 0) {
                toast.error("Minst en transaktion krävs");
                return;
            }

            // Check if transactions are balanced
            const totalDebit = verificationData.transactions
                .filter(t => t.transaction_type === 'debit')
                .reduce((sum, t) => sum + Number(t.amount) || 0, 0);

            const totalCredit = verificationData.transactions
                .filter(t => t.transaction_type === 'credit')
                .reduce((sum, t) => sum + Number(t.amount) || 0, 0);

            if (Math.abs(totalDebit - totalCredit) > 0.01) {
                toast.error("Transaktioner måste balansera");
                return;
            }

            // Call the backend API
            const createVerificationApiUrl = getApiUrl(`${companyId}/verifications`);
            const response = await axios.post(createVerificationApiUrl, verificationData, {
                withCredentials: true
            });

            console.log('Create verification response:', response.data);

            if (response.data.success) {
                toast.success("Verifikat skapat framgångsrikt");

                // Bump the per-tenant usage counter so the picker can
                // surface this template under "Senast använda" (DEFERRED 48).
                // entry.template is the templateId string set by
                // handleTemplateSelect — UUID for backend rows, an
                // arbitrary string for the in-file hardcoded fallback
                // catalog (no UUID → no usage to record).
                if (typeof entry.template === 'string' && /^[0-9a-f-]{36}$/i.test(entry.template)) {
                    // Fire-and-forget: the service swallows failures so
                    // a flaky network never overshadows booking success.
                    recordTemplateUsage(companyId, entry.template);
                }

                // Reset the form
                setEntry({
                    entryType: null,
                    browseAllTemplates: false,
                    template: null,
                    templateData: null,
                    attachments: [],
                    date: new Date(),
                    description: "",
                    reference: "",
                    notes: "",
                    parameters: {},
                    userDefinedAccounts: {},
                    bookingProposal: null,
                    customJournalEntries: null,
                    templateIncompatibility: null
                });

                setActiveStep(STEPS.ENTRY_TYPE);
            } else {
                toast.error("Fel vid skapande av verifikat");
            }

        } catch (error) {
            console.error('Fel vid skapande av verifikat:', error);

            if (error.response && error.response.status === 500) {
                toast.error(error.response.data?.message || "Serverfel uppstod");
            } else if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Fel vid skapande av verifikat");
            }
        } finally {
            setOperationInProgress(false);
        }
    };

    // Helper functions (keep existing helper functions)
    // const transformEntryToVerification = (entry) => {
    //     const journalEntries = entry.customJournalEntries || generateJournalEntriesFromTemplate(entry);

    //     const transactions = journalEntries.map(journalEntry => {
    //         const account = getAccountByNumber ? getAccountByNumber(journalEntry.account) :
    //             accounts.find(acc => acc.value === journalEntry.account || acc.accountNumber === journalEntry.account);

    //         if (!account) {
    //             throw new Error(`Konto hittades inte: ${journalEntry.account}`);
    //         }

    //         return {
    //             account_id: account.account_id || account.accountNumber,
    //             amount: journalEntry.debit > 0 ? journalEntry.debit : journalEntry.credit,
    //             transaction_type: journalEntry.debit > 0 ? 'debit' : 'credit',
    //             debit: journalEntry.debit,
    //             credit: journalEntry.credit,
    //             description: journalEntry.description || entry.description,
    //             transaction_description: journalEntry.description || entry.description
    //         };
    //     });

    //     const transformedAttachments = entry.attachments.map(attachment => ({
    //         attachmentId: attachment.id,
    //         file_url: attachment.file_url || attachment.url,
    //         file_name: attachment.name,
    //         file_type: attachment.type,
    //         file_size: attachment.size,
    //         blob_name: attachment.blob_name || attachment.blobName
    //     }));

    //     return {
    //         date: entry.date.toISOString().split('T')[0],
    //         description: entry.description,
    //         series: 'A',
    //         transactions: transactions,
    //         attachments: transformedAttachments,
    //         reference_data: null
    //     };
    // };

    const transformEntryToVerification = (entry) => {
        const journalEntries = entry.customJournalEntries || generateJournalEntriesFromTemplate(entry);

        const transactions = journalEntries.map(journalEntry => {
            const account = getAccountByNumber ? getAccountByNumber(journalEntry.account) :
                accounts.find(acc => acc.value === journalEntry.account || acc.accountNumber === journalEntry.account);

            if (!account) {
                throw new Error(`Konto hittades inte: ${journalEntry.account}`);
            }

            return {
                account_id: account.account_id || account.accountNumber,
                amount: journalEntry.debit > 0 ? journalEntry.debit : journalEntry.credit,
                transaction_type: journalEntry.debit > 0 ? 'debit' : 'credit',
                debit: journalEntry.debit,
                credit: journalEntry.credit,
                description: journalEntry.description || entry.description,
                transaction_description: journalEntry.description || entry.description
            };
        });

        // Enhanced attachment transformation with proper metadata
        const transformedAttachments = entry.attachments.map(attachment => ({
            attachmentId: attachment.attachmentId || attachment.id,
            file_url: attachment.url || attachment.file_url,
            file_name: attachment.name,
            file_type: attachment.type,
            file_size: attachment.size,
            blob_name: attachment.blobName || attachment.blob_name
        }));

        console.log('Transformed attachments:', transformedAttachments);

        return {
            date: entry.date.toISOString().split('T')[0],
            description: entry.description,
            reference: entry.reference || '',
            notes: entry.notes || '',
            series: 'V',
            transactions: transactions,
            attachments: transformedAttachments,
            reference_data: entry.templateData?.body ? {
                type: 'bookkeeping_template',
                id: entry.template,
                template_id: entry.template,
                inputs: entry.parameters || {},
            } : null
        };
    };

    const generateJournalEntriesFromTemplate = (entry) => {
        if (!entry.templateData || !entry.templateData.transactions) {
            return [];
        }

        const entries = [];

        entry.templateData.transactions.forEach((transaction, index) => {
            let accountNumber = transaction.account_number;
            let accountName = transaction.account_name;

            if (transaction.is_user_defined) {
                const userAccountKey = `user_account_${index}`;
                const selectedAccount = entry.userDefinedAccounts[userAccountKey];
                if (selectedAccount) {
                    accountNumber = selectedAccount;
                    const accountInfo = getAccountByNumber ? getAccountByNumber(selectedAccount) :
                        accounts.find(acc => acc.value === selectedAccount);
                    accountName = accountInfo ? accountInfo.accountName : `Konto ${selectedAccount}`;
                } else {
                    throw new Error(`Användardefinierat konto inte valt för transaktion ${index}`);
                }
            }

            const debitAmount = transaction.debit_formula
                ? evaluateFormula(transaction.debit_formula, entry.parameters)
                : 0;

            const creditAmount = transaction.credit_formula
                ? evaluateFormula(transaction.credit_formula, entry.parameters)
                : 0;

            if (debitAmount > 0 || creditAmount > 0) {
                entries.push({
                    account: accountNumber,
                    accountName: accountName,
                    debit: debitAmount,
                    credit: creditAmount,
                    description: transaction.description || entry.description
                });
            }
        });

        return entries;
    };

    const evaluateFormula = (formula, parameters) => {
        if (!formula) return 0;

        try {
            let expression = formula;
            Object.keys(parameters).forEach(param => {
                const value = parameters[param] || 0;
                expression = expression.replace(new RegExp(param, 'g'), value);
            });

            return eval(expression);
        } catch (error) {
            console.error('Fel vid utvärdering av formel:', formula, error);
            return 0;
        }
    };

    const handleTemplateSelect = async (templateId) => {
        const selectedTemplate = templates.find(t => t.id === templateId);

        // Hardcoded fallback templates (used when API load failed) have
        // no originalData and no fetchable id.
        if (!selectedTemplate || !selectedTemplate.originalData) {
            setEntry(prev => ({
                ...prev,
                template: templateId,
                templateData: null,
                parameters: { amount: 0 },
                userDefinedAccounts: {},
                bookingProposal: null,
                customJournalEntries: null,
                templateIncompatibility: null
            }));
            setActiveStep(STEPS.DETAILS);
            return;
        }

        // M1 — listTemplates is picker-projected (no body).
        // Fetch the full template (including body) by id when the user
        // selects one. Only API-backed templates have a UUID id; the
        // hardcoded fallbacks were short-circuited above.
        setOperationInProgress(true);
        let data = selectedTemplate.originalData;
        try {
            if (/^[0-9a-f-]{36}$/i.test(templateId)) {
                data = await getBookkeepingTemplate(companyId, templateId);
            }
        } catch (err) {
            console.error('Error fetching template body:', err);
            toast.error('Det gick inte att ladda mallen. Försök igen.');
            setOperationInProgress(false);
            return;
        }
        setOperationInProgress(false);

        // New-shape templates carry `body.inputs` and `body.lines`.
        // Step-3 / step-4 will route through TemplateForm +
        // TemplatePreview against the /suggest endpoint; the
        // values object is keyed by input.name (no separate
        // userDefinedAccounts bucket — account_picker / select
        // inputs go in `parameters` like everything else).
        if (data.body && Array.isArray(data.body.inputs)) {
            const initialParameters = {};
            // `example` is documentation and becomes the field's placeholder;
            // prefilling it as a value made 5 000 kr look like a real amount.
            data.body.inputs.forEach((inp) => {
                if (inp.default !== undefined) initialParameters[inp.name] = inp.default;
            });
            setEntry(prev => ({
                ...prev,
                template: templateId,
                templateData: data,
                parameters: initialParameters,
                userDefinedAccounts: {},
                bookingProposal: null,
                customJournalEntries: null,
                templateIncompatibility: null
            }));
            setActiveStep(STEPS.DETAILS);
            return;
        }

        // Legacy shape: parameters + transactions arrays.
        const initialParameters = {};
        (data.parameters || []).forEach(param => {
            initialParameters[param.parameter_name] = param.parameter_type === 'Number' ? 0 : '';
        });

        const initialUserAccounts = {};
        (data.transactions || [])
            .filter(t => t.is_user_defined)
            .forEach((t, index) => {
                initialUserAccounts[`user_account_${index}`] = '';
            });

        setEntry(prev => ({
            ...prev,
            template: templateId,
            templateData: data,
            parameters: initialParameters,
            userDefinedAccounts: initialUserAccounts,
            bookingProposal: null,
            customJournalEntries: null,
            templateIncompatibility: null
        }));
        setActiveStep(STEPS.DETAILS);
    };

    const handleEntryTypeSelect = (entryType) => {
        setEntry(prev => ({
            ...prev,
            entryType,
            browseAllTemplates: false,
            template: null,
            templateData: null,
            parameters: {},
            userDefinedAccounts: {},
            bookingProposal: null,
            customJournalEntries: null,
            templateIncompatibility: null
        }));
        setActiveStep(STEPS.TEMPLATE);
    };

    // Helper functions for accounts
    const getAccountByNumber = (accountNumber) => {
        return accounts.find(acc => acc.accountNumber === accountNumber);
    };

    const getAccountsByType = (accountType) => {
        return accounts.filter(acc => acc.accountType === accountType);
    };

    // The default picker view; every account the entry points at stays
    // selectable — proposal rows, template inputs and legacy selections
    // alike (review 2026-09-05, F11).
    const pickerAccounts = useMemo(
        () => buildAccountPickerOptions(accounts, {
            showAll: showAllAccounts,
            pinned: pinnedAccountValues(entry),
        }),
        [accounts, showAllAccounts, entry.customJournalEntries, entry.parameters, entry.userDefinedAccounts]
    );
    const accountScope = {
        showAll: showAllAccounts,
        onToggle: () => setShowAllAccounts((v) => !v),
        defaultCount: countDefaultPickerAccounts(accounts),
        totalCount: accounts.length,
        hasCompanyAccounts: hasCompanyAccounts(accounts),
    };

    // Typing widens to the whole chart (number, chart name, imported label —
    // the shared matcher, UX_PLAN.md P1); an empty search shows the default view.
    const searchAccounts = (searchTerm) => {
        if (!searchTerm) return pickerAccounts;
        return searchAccountOptions(accounts, searchTerm);
    };

    // Keep existing helper functions for hardcoded data
    // Discovery-only event family. It narrows the picker without changing
    // the template DSL's accounting type or implying a cash direction.
    const mapTemplateToEventFamily = (template) => {
        const category = String(template.category || '').toLowerCase();
        const type = String(template.template_type || '').toLowerCase();
        if (category === 'forsaljning' || type === 'income') return 'sale';
        if (['inkop_varor', 'inkop_tjanster', 'inventarier', 'bil_transport', 'representation', 'utlagg'].includes(category)
            || ['expense', 'investment'].includes(type)) return 'purchase';
        if (category === 'personal_lon' || type === 'payroll') return 'payroll';
        if (category === 'skatt_moms' || ['tax', 'taxes'].includes(type)) return 'tax';
        if (category === 'eget_kapital' || type === 'capital_movement') return 'capital';
        if (type === 'transfer') return 'transfer';
        if (category === 'periodiseringar' || type === 'year_end') return 'adjustment';
        if (category === 'bank_avgifter') return 'finance';
        return 'other';
    };

    const mapApiTypeToCategory = (apiType) => {
        if (!apiType) return 'other';
        const key = String(apiType).toLowerCase();
        const mapping = {
            'income': 'sales',
            'expense': 'purchases',
            'transfer': 'banking',
            'investment': 'purchases',
            'tax': 'taxes',
            'payroll': 'salary',
            'capital_movement': 'accounting',
            'year_end': 'accounting',
            'advanced': 'other',
            'taxes': 'taxes',
            'other': 'other'
        };
        return mapping[key] || 'other';
    };

    const isCommonTemplate = (templateName) => {
        const commonNames = [
            'Försäljningsintäkter',
            'Kontorsmaterial',
            'Parkeringsavgift',
            'IT-produkter',
            'Friskvårdsbidrag'
        ];
        return commonNames.some(name =>
            templateName.toLowerCase().includes(name.toLowerCase())
        );
    };

    const extractAccountsFromTemplate = (template) => {
        // New-shape templates carry the line set on `body.lines` (each
        // line has `account` OR `account_ref`). Legacy templates carry
        // a top-level `transactions[]` array. Handle both.
        if (template.body && Array.isArray(template.body.lines)) {
            return template.body.lines
                .map((l) => l.account)
                .filter(Boolean)
                .map((acct) => `${acct}`);
        }
        if (template.transactions && Array.isArray(template.transactions)) {
            return template.transactions
                .filter(t => t.account_number && !t.is_user_defined)
                .map(t => `${t.account_number} - ${t.account_name}`)
                .filter(Boolean);
        }
        return ["Diverse"];
    };

    // Show loading state while fetching initial data
    if ((templatesLoading && templates.length === 0) || (accountsLoading && accounts.length === 0)) {
        return (
            <div className="bookkeeping-wizard-container">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border mb-3" role="status">
                            <span className="visually-hidden">Laddar...</span>
                        </div>
                        <p className="text-muted">Laddar bokföringsguide...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .bookkeeping-wizard-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 2rem 1rem;
                }

                /* Enhanced step styling for better visual hierarchy */
                .enhanced-step-content {
;
                    display: flex;
                    flex-direction: column;
                }

                /* Info cards for better UX */
                .info-card {
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--rgba-primary-2);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1.5rem;
                    display: flex;
                    gap: 0.75rem;
                }

                .info-icon {
                    color: var(--bs-primary);
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }

                .info-text {
                    font-size: 0.875rem;
                    color: var(--primary-dark);
                    line-height: 1.5;
                    margin: 0;
                }

                .warning-card {
                    background: rgba(255, 170, 43, 0.08);
                    border: 1px solid rgba(255, 170, 43, 0.3);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1.5rem;
                    display: flex;
                    gap: 0.75rem;
                }

                .warning-icon {
                    color: var(--bs-warning);
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }

                .warning-text {
                    font-size: 0.875rem;
                    color: var(--bs-dark);
                    line-height: 1.5;
                    margin: 0;
                }

                /* Enhanced form styling */
                .form-section {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    border: 1px solid #e2e8f0;
                }

                .section-icon {
                    width: 24px;
                    height: 24px;
                    color: #64748b;
                }

                /* Mobile responsiveness */
                @media (max-width: 768px) {
                    .bookkeeping-wizard-container {
                        padding: 1rem 0.5rem;
                    }
                    
                    .form-section {
                        padding: 1rem;
                    }
                }

                /* Smooth transitions for all interactive elements */
                * {
                    transition: all 0.2s ease;
                }

                /* Focus states for accessibility */
                .nav-button:focus,
                .step-item:focus {
                    outline: 2px solid var(--bs-primary);
                    outline-offset: 2px;
                }
            `}</style>

            <PageHeader
              icon={FilePlus}
              title="Nytt verifikat"
              subtitle="LedgerLoop föreslår konteringen — du granskar och bokför."
            />

            <div className="">
                <BookkeepingProgressTracker
                    activeStep={activeStep}
                    entry={entry}
                    onStepClick={handleStepClick}
                    canNavigateToStep={canNavigateToStep}
                    isStepCompleted={isStepCompleted}
                />

                <BookkeepingStepContent
                    activeStep={activeStep}
                    entry={entry}
                    setEntry={setEntry}
                    templates={templates}
                    accounts={pickerAccounts}
                    allAccounts={accounts}
                    accountScope={accountScope}
                    templatesLoading={templatesLoading}
                    accountsLoading={accountsLoading}
                    templatesError={templatesError}
                    accountsError={accountsError}
                    companyId={companyId}
                    handleTemplateSelect={handleTemplateSelect}
                    fetchTemplates={fetchTemplates}
                    getAccountByNumber={getAccountByNumber}
                    getAccountsByType={getAccountsByType}
                    searchAccounts={searchAccounts}
                    operationInProgress={operationInProgress}
                    onEntryTypeSelect={handleEntryTypeSelect}
                    onBrowseAllTemplates={() => {
                        setEntry(prev => ({ ...prev, entryType: null, browseAllTemplates: true }));
                        setActiveStep(STEPS.TEMPLATE);
                    }}
                />

                <BookkeepingNavigationBar
                    activeStep={activeStep}
                    onStepChange={handleStepChange}
                    operationInProgress={operationInProgress}
                    canProceedToNext={canProceedToNext()}
                    canGoBack={canGoBack()}
                    getNextStep={getNextStep}
                    getPreviousStep={getPreviousStep}
                    onCreateVerification={handleCreateVerification}
                    canCreateVerification={!entry.templateData?.body
                        || (!!entry.bookingProposal && !!entry.customJournalEntries
                            && !entry.templateIncompatibility?.length)}
                />
            </div>
        </>
    );
};

export default BookkeepingEntryWizard;
